import { Box } from '@mui/material'
import { keyframes, SxProps } from '@mui/system'
import { FC } from 'react'

import { EmphasisRarityId } from './index'

/**
 * アイテム強調表示用フレーム
 * - 黄色に発光
 */
export const LightFrame: FC<{ emphasisRarityId?: string }> = ({
  emphasisRarityId = 'common',
}) => {
  return (
    <Box
      id="grow-light-frame"
      sx={{
        ...commonFrameStyle,
        ...getEmphasisRaritySx(emphasisRarityId),
      }}
    />
  )
}

/**
 * アニメーション付きのアイテム強調表示用フレーム
 * - 白に発光後、レアであれば黄色に発光
 */
export const AnimationGrowLightCell: FC<{
  flipped: boolean
  emphasisRarityId: EmphasisRarityId
  animationTime: number
}> = ({ flipped, emphasisRarityId, animationTime }) => {
  const growLight = growLightKeyframe(emphasisRarityId)

  return (
    <Box
      id="grow-light-wrapper"
      sx={{
        ...commonFrameStyle,
        willChange: 'box-shadow, background-color, z-index',
        backfaceVisibility: 'hidden',
        animation: flipped
          ? `${growLight} ${animationTime}ms ease both`
          : undefined,
      }}
    />
  )
}

/**
 * アニメーション付きのアイテム大強調表示用フレーム
 * - 白に画面全体サイズ発光後、黄色に発光
 */
export const AnimationRareGrowLightCell: FC<{
  flipped: boolean
  emphasisRarityId: EmphasisRarityId
  animationTime: number
}> = ({ flipped, emphasisRarityId, animationTime }) => {
  const rareGrowLight = rareGrowLightKeyframe(emphasisRarityId)

  return (
    <Box
      id="rare-grow-Light-frame"
      sx={{
        ...commonFrameStyle,
        willChange: 'box-shadow, background-color, z-index',
        backfaceVisibility: 'hidden',
        animation: flipped
          ? `${rareGrowLight} ${animationTime}ms ease both`
          : undefined,
      }}
    />
  )
}

const commonFrameStyle: SxProps = {
  position: 'absolute',
  borderRadius: 1,
  height: 115,
  width: 72,
}

const growLightKeyframe = (emphasisRarityId: EmphasisRarityId) => {
  const { boxShadow, background, filter } =
    getEmphasisRaritySx(emphasisRarityId)
  return keyframes`
  0% { 
    box-shadow: 
      inset 0 0 120px whitesmoke,
      0 0 200px white;
    background: white;
    z-index: 1;
  }
  30% {
    box-shadow: 
      inset 0 0 80px whitesmoke,
      0 0 80px white;
    background: white;
    z-index: 1;
  }
  60%{
    box-shadow: none;
    background: white;
    z-index: 1;
  }
  100% {  
    box-shadow: ${boxShadow};
    background: ${background};
    filter: ${filter};
  }
`
}

const rareGrowLightKeyframe = (emphasisRarityId: EmphasisRarityId) => {
  const { boxShadow, background, filter } =
    getEmphasisRaritySx(emphasisRarityId)
  return keyframes`
  0% { 
    box-shadow: 
      inset 0 0 200px 200px whitesmoke,
      0 0 200px white;
    background: white;
    z-index: 1;
  }
  15% {
    box-shadow: 
      inset 0 0 80px 80px whitesmoke,
      0 0 80px white;
    background: white;
    z-index: 1;
  }
  50%{
    box-shadow: 0 0 80px 1000px white;
    background: white;
    z-index: 1;
  }
  90%{
    box-shadow: 0 0 80px 1000px white;
    background: white;
    z-index: 1;
  }
  91%{
    box-shadow: none;
  }
  100% {
    box-shadow: ${boxShadow};
    background: ${background};
    filter: ${filter};
  }
`
}

/**
 * return emphasis rarity style
 * @param emphasisRarityId
 * @param size
 */
export function getEmphasisRaritySx(
  emphasisRarityId: string | undefined,
  size = 20
) {
  return (
    {
      common: { boxShadow: 'none' },
      rare: { boxShadow: `0px 0px ${size}px 0px rgba(255, 139, 3, 0.80)` },
      superrare: { boxShadow: `0px 0px ${size}px 0px rgba(255, 15, 0, 0.80)` },
      ultrarare: {
        background:
          'conic-gradient(from 90deg at 50% 50%, rgba(255, 7, 147, 0.70) 0deg, rgba(255, 0, 9, 0.70) 52.77951657772064deg, rgba(255, 132, 0, 0.70) 130.75308680534363deg, rgba(255, 224, 0, 0.70) 179.44134950637817deg, rgba(113, 255, 12, 0.70) 235.81891536712646deg, rgba(0, 203, 255, 0.70) 307.67319202423096deg, rgba(154, 36, 255, 0.70) 360deg)',
        filter: 'blur(10px)',
      },
      limited: {
        background:
          'conic-gradient(from 90deg at 50% 50%, #5100FF 0deg, #EB00FF 52.78deg, #5100FF 130.75deg, #EB00FF 179.44deg, #5100FF 235.82deg, #EB00FF 307.67deg, #5100FF 360deg)',
        filter: 'blur(10px)',
      },
    }[emphasisRarityId ?? 'common'] || {}
  )
}
